import { EditorOnly, useCanEdit } from '../components/EditorOnly';
import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
} from '@mui/material';
import { Stack, Typography } from '../components/mui-system';
import { api } from '../api/client';
import type { TelegramChannel } from '../api/types';
import {
  DONOR_REPORT_CATEGORIES,
  type DonorDeliveryView,
  type DonorReportCategory,
  type DonorReportSettingsInput,
  type DonorReportSettingsView,
} from '../../../api/src/donors/donor-reports.types';

const labels: Record<DonorReportCategory, string> = {
  missing: 'Пропавшие и не найденные ссылки',
  restored: 'Восстановленные ссылки',
  changed: 'Изменения URL, анкора и rel',
  errors: 'Ошибки проверки',
  overdue: 'Просроченные проверки',
};
const message = (error: unknown) => (error instanceof Error ? error.message : 'Ошибка запроса');
function config(view: DonorReportSettingsView): DonorReportSettingsInput {
  return {
    revision: view.revision,
    enabled: view.enabled,
    sendTime: view.sendTime,
    timeZone: view.timeZone,
    channelId: view.channelId,
    topicId: view.topicId,
    categories: view.categories,
  };
}
export function DonorSettingsDialog({
  workspace,
  onClose,
}: {
  workspace: string;
  onClose: () => void;
}) {
  const canViewTelegram = useCanEdit('telegram.view');
  const editable = useCanEdit('donors.settings', 'donors.schedule', 'donors.send');
  const [saved, setSaved] = useState<DonorReportSettingsView | null>(null);
  const [draft, setDraft] = useState<DonorReportSettingsInput | null>(null);
  const [channels, setChannels] = useState<TelegramChannel[]>([]);
  const [title, setTitle] = useState('');
  const [chatId, setChatId] = useState('');
  const [token, setToken] = useState('');
  const [connectionEnabled, setConnectionEnabled] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  function choose(channel: TelegramChannel | undefined) {
    setTitle(channel?.title ?? '');
    setChatId(channel?.chatId ?? '');
    setToken('');
    setConnectionEnabled(channel?.enabled ?? true);
  }
  useEffect(() => {
    let current = true;
    void Promise.all([
      api.donorSettings(workspace),
      canViewTelegram ? api.telegram(workspace) : Promise.resolve([]),
    ])
      .then(([view, rows]) => {
        if (current) {
          setSaved(view);
          setDraft(config(view));
          setChannels(rows);
          choose(rows.find((row) => row.id === view.channelId));
        }
      })
      .catch((caught: unknown) => {
        if (current) setError(message(caught));
      });
    return () => {
      current = false;
    };
  }, [workspace, canViewTelegram]);
  useEffect(() => {
    let current = true;
    const interval = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      void api
        .donorSettings(workspace)
        .then((view) => {
          if (current)
            setSaved((previous) =>
              previous
                ? {
                    ...previous,
                    lastDaily: view.lastDaily,
                    lastTest: view.lastTest,
                    nextSendAt: view.nextSendAt,
                  }
                : previous,
            );
        })
        .catch((caught: unknown) => {
          if (current) setError(message(caught));
        });
    }, 5000);
    return () => {
      current = false;
      clearInterval(interval);
    };
  }, [workspace, canViewTelegram]);
  const selected = channels.find((row) => row.id === draft?.channelId);
  const connectionChanged =
    title !== (selected?.title ?? '') ||
    chatId !== (selected?.chatId ?? '') ||
    Boolean(token) ||
    connectionEnabled !== (selected?.enabled ?? true);
  const dirty = Boolean(
    saved &&
    draft &&
    (JSON.stringify(draft) !== JSON.stringify(config(saved)) || connectionChanged),
  );
  async function save() {
    if (!draft) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const connection = connectionChanged
        ? {
            id: selected?.id,
            title,
            chatId,
            botToken: token || undefined,
            enabled: connectionEnabled,
          }
        : undefined;
      const view = await api.saveDonorSettings(workspace, { ...draft, connection });
      // Clear the secret immediately, even if refreshing the channel list fails.
      setToken('');
      setSaved(view);
      setDraft(config(view));
      const rows = await api.telegram(workspace);
      setChannels(rows);
      choose(rows.find((row) => row.id === view.channelId));
      setNotice('Настройки сохранены.');
    } catch (caught) {
      setError(message(caught));
    } finally {
      setBusy(false);
    }
  }
  async function test() {
    if (!saved || dirty) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      setSaved(await api.testDonorTelegram(workspace, saved.revision));
      setNotice('Тестовое сообщение поставлено в очередь. Результат появится ниже.');
    } catch (caught) {
      setError(message(caught));
    } finally {
      setBusy(false);
    }
  }
  const testPending =
    saved?.lastTest && ['pending', 'sending', 'retrying'].includes(saved.lastTest.status);
  return (
    <Dialog open fullWidth maxWidth="md" onClose={() => !busy && onClose()}>
      <DialogTitle>Настройки доноров</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {notice && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {notice}
          </Alert>
        )}
        {!draft || !saved ? (
          !error && <CircularProgress aria-label="Загрузка настроек" />
        ) : (
          <Stack gap={2} mt={1}>
            <Typography variant="h3">Отчётность</Typography>
            <FormControlLabel
              label="Отправлять ежедневную сводку"
              control={
                <Switch
                  checked={draft.enabled}
                  disabled={!editable || busy}
                  onChange={(_, enabled) => setDraft({ ...draft, enabled })}
                />
              }
            />
            <Typography variant="body2" color="text.secondary">
              Сводка приходит каждый день, даже без изменений. В ней всегда есть общий охват и
              состояние мониторинга.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
              <TextField
                label="Время отправки"
                type="time"
                value={draft.sendTime}
                disabled={!editable || busy}
                slotProps={{ inputLabel: { shrink: true } }}
                onChange={(event) => setDraft({ ...draft, sendTime: event.target.value })}
              />
              <TextField
                label="Часовой пояс"
                value={draft.timeZone}
                disabled={!editable || busy}
                fullWidth
                helperText="Название IANA, например Europe/Moscow, Europe/Berlin или Asia/Almaty"
                onChange={(event) => setDraft({ ...draft, timeZone: event.target.value })}
                slotProps={{ htmlInput: { maxLength: 80 } }}
              />
            </Stack>
            <Divider />
            <Typography variant="h3">Telegram</Typography>
            {!saved.encryptionReady && (
              <Alert severity="warning">
                На сервере не настроено шифрование токенов: нужен APP_ENCRYPTION_KEY. Сохранение
                токена и отправка будут недоступны.
              </Alert>
            )}
            <TextField
              select
              label="Бот / подключение"
              value={draft.channelId ?? ''}
              disabled={!editable || busy}
              fullWidth
              slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
              onChange={(event) => {
                const id = event.target.value || null;
                setDraft({ ...draft, channelId: id });
                choose(channels.find((row) => row.id === id));
                setNotice('');
              }}
            >
              <MenuItem value="">Новое подключение</MenuItem>
              {channels.map((row) => (
                <MenuItem value={row.id} key={row.id}>
                  {row.title} · {row.chatId}
                  {row.enabled ? '' : ' · отключено'}
                </MenuItem>
              ))}
            </TextField>
            {selected && (
              <Typography fontSize={12} color="text.secondary">
                Это подключение также доступно в разделе «Телеграм». Изменения бота, токена и chat
                ID сохраняются для него целиком.
              </Typography>
            )}
            <TextField
              label="Название бота / подключения"
              value={title}
              disabled={!editable || busy}
              onChange={(event) => setTitle(event.target.value)}
              slotProps={{ htmlInput: { maxLength: 80 } }}
            />
            <TextField
              label="Токен бота"
              type="password"
              autoComplete="new-password"
              value={token}
              disabled={!editable || busy || !saved.encryptionReady}
              onChange={(event) => setToken(event.target.value)}
              helperText={
                selected?.hasBotToken
                  ? 'Токен сохранён и скрыт. Оставьте поле пустым, чтобы сохранить его, или введите новый для замены.'
                  : 'Токен шифруется перед записью в БД и не возвращается в браузер.'
              }
              slotProps={{ htmlInput: { maxLength: 180 } }}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
              <TextField
                label="Chat ID"
                value={chatId}
                disabled={!editable || busy}
                fullWidth
                helperText="Числовой ID чата или @username канала"
                onChange={(event) => setChatId(event.target.value)}
                slotProps={{ htmlInput: { maxLength: 80 } }}
              />
              <TextField
                label="ID темы (необязательно)"
                type="number"
                value={draft.topicId ?? ''}
                disabled={!editable || busy}
                fullWidth
                helperText="Для отправки в конкретную тему форума"
                onChange={(event) =>
                  setDraft({
                    ...draft,
                    topicId: event.target.value ? Number(event.target.value) : null,
                  })
                }
                slotProps={{ htmlInput: { min: 1, max: 2147483647 } }}
              />
            </Stack>
            <FormControlLabel
              label="Telegram-подключение активно"
              control={
                <Switch
                  checked={connectionEnabled}
                  disabled={!editable || busy}
                  onChange={(_, value) => setConnectionEnabled(value)}
                />
              }
            />
            <Divider />
            <Typography variant="h3">Состав сводки</Typography>
            <Stack gap={0}>
              {DONOR_REPORT_CATEGORIES.map((category) => (
                <FormControlLabel
                  key={category}
                  label={labels[category]}
                  control={
                    <Checkbox
                      checked={draft.categories.includes(category)}
                      disabled={!editable || busy}
                      onChange={(_, checked) =>
                        setDraft({
                          ...draft,
                          categories: checked
                            ? DONOR_REPORT_CATEGORIES.filter(
                                (item) => item === category || draft.categories.includes(item),
                              )
                            : draft.categories.filter((item) => item !== category),
                        })
                      }
                    />
                  }
                />
              ))}
            </Stack>
            <Divider />
            <Typography variant="h3">Диагностика</Typography>
            <Typography variant="body2">
              Следующая сводка:{' '}
              {saved.nextSendAt
                ? new Date(saved.nextSendAt).toLocaleString('ru-RU', { timeZone: saved.timeZone }) +
                  ` (${saved.timeZone})`
                : 'отчётность выключена'}
              .
            </Typography>
            <Delivery label="Последняя ежедневная сводка" value={saved.lastDaily} />
            <Delivery label="Последнее тестовое сообщение" value={saved.lastTest} />
            <EditorOnly permissions="donors.send">
              <Button
                variant="outlined"
                disabled={
                  busy ||
                  dirty ||
                  !saved.channelId ||
                  !saved.encryptionReady ||
                  !selected?.enabled ||
                  !selected.hasBotToken ||
                  Boolean(testPending)
                }
                onClick={() => void test()}
              >
                Отправить тестовое сообщение
              </Button>
            </EditorOnly>
            {dirty && (
              <Typography fontSize={12} color="text.secondary">
                Сначала сохраните изменения. Тест отправляется по сохранённым настройкам.
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>
          Закрыть
        </Button>
        <EditorOnly permissions={['donors.settings', 'donors.schedule', 'donors.send']}>
          <Button
            variant="contained"
            disabled={busy || !draft || (!dirty && saved?.revision !== 0)}
            onClick={() => void save()}
          >
            {busy ? 'Обрабатываем…' : 'Сохранить настройки'}
          </Button>
        </EditorOnly>
      </DialogActions>
    </Dialog>
  );
}
function Delivery({ label, value }: { label: string; value: DonorDeliveryView | null }) {
  const statuses = {
    pending: 'В очереди',
    sending: 'Отправляется',
    retrying: 'Ожидает повтора',
    sent: 'Доставлено',
    failed: 'Ошибка доставки',
    unknown: 'Результат неизвестен',
    cancelled: 'Отменено',
  };
  return (
    <Stack gap={0.5}>
      <Typography fontWeight={600}>{label}</Typography>
      {!value ? (
        <Typography variant="body2">Отправок пока не было.</Typography>
      ) : (
        <>
          <Typography variant="body2">
            {statuses[value.status]} ·{' '}
            {new Date(value.sentAt ?? value.attemptedAt ?? value.createdAt).toLocaleString('ru-RU')}
          </Typography>
          <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
            Получатель: {value.chatId || 'не настроен'}
            {value.topicId ? ` · тема ${value.topicId}` : ''}
          </Typography>
          {value.error && (
            <Alert severity={value.status === 'failed' ? 'error' : 'warning'}>{value.error}</Alert>
          )}
        </>
      )}
    </Stack>
  );
}
